import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { STATIONS, Station, TimestampEntry, UserRole } from '../types';
import { CrewIcon, PilotIcon, CameraIcon, XCircleIcon } from '../components/Icons';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import NewsInputField from '../components/NewsInputField';

const CrewMovementScreen: React.FC = () => {
  const { selectedFlightData: flightData, updateCrewMovement, clearCrewMovement, updateFlightData, updateCounterDistributionField, addCapturedDoc, showNotification, logFinalizeTimestamp, clearSystemTimestamps, clearGateTimestamps, updateAtsrField } = useFlight();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [clearConfirm, setClearConfirm] = useState<{ station: Station; type: 'crew' | 'pilots' } | null>(null);
  const [confirmAction, setConfirmAction] = useState<'systemClear' | 'gateClear' | null>(null);
  const [flightNumbers, setFlightNumbers] = useState<string[]>([]);

  useEffect(() => {
    const flightNumbersRef = ref(database, 'config/flightNumbers');
    const unsubscribe = onValue(flightNumbersRef, (snapshot) => {
        setFlightNumbers(snapshot.val() || []);
    });

    return () => unsubscribe();
  }, []);

  const confirmText = useMemo(() => {
    switch (confirmAction) {
        case 'systemClear': return { title: 'Confirm System Clear', body: 'This will clear ALL crew and pilot timestamps for the current flight. This action cannot be undone. Are you sure?' };
        case 'gateClear': return { title: 'Confirm Gate Clear', body: 'This will clear all timestamps from "At Gate" onwards. This action cannot be undone. Are you sure?' };
        default: return { title: '', body: '' };
    }
  }, [confirmAction]);

  if (!flightData) return null;
  
  const etd = flightData.counterDistribution.etd || '';
  const etdDate = etd.split('T')[0] || '';
  
  const getLatestTimestamp = (station: Station, type: 'crew' | 'pilots'): TimestampEntry | null => {
      const entries = flightData.crewMovement[station]?.[type];
      return entries && entries.length > 0 ? entries[entries.length - 1] : null;
  }

  const handleDateChange = (date: string) => {
    if (date) {
      updateCounterDistributionField('root', 'etd', `${date}T00:00:00`);
    } else {
      updateCounterDistributionField('root', 'etd', '');
    }
  };

  const handleTimestamp = (station: Station, type: 'crew' | 'pilots') => {
    updateCrewMovement(station, type);
    showNotification(`${type === 'crew' ? 'Crew' : 'Pilot'} time for ${station} recorded.`);
  };
  
  const handleClearTimestamp = () => {
    if (clearConfirm) {
        clearCrewMovement(clearConfirm.station, clearConfirm.type);
        showNotification(`Latest ${clearConfirm.type} time for ${clearConfirm.station} cleared.`);
        setClearConfirm(null);
    }
  };

  const handleCheckboxChange = (key: 'immigrationApproved' | 'crewAndPilotsArrivedTogether', checked: boolean) => {
    updateFlightData(key, checked);
    if (key === 'immigrationApproved') {
        showNotification('Immigration status updated.');
    } else {
        showNotification('Paired arrival mode updated.');
    }
  };

  const handleCaptureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsCapturing(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const newDoc = {
          url: reader.result as string,
          capturedAt: new Date().toISOString(),
          by: user?.username || 'Unknown',
        };
        addCapturedDoc(newDoc);
        setIsCapturing(false);
        showNotification('Document captured successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirmAction = () => {
    switch (confirmAction) {
        case 'systemClear':
            clearSystemTimestamps();
            break;
        case 'gateClear':
            clearGateTimestamps();
            break;
    }
    setConfirmAction(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h2 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Flight Setup</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="flightNoSelect" className="block text-sm font-medium text-slate-400 mb-1">Flight Number</label>
            <select
              id="flightNoSelect"
              value={flightData.counterDistribution.flightNo}
              onChange={(e) => updateCounterDistributionField('root', 'flightNo', e.target.value)}
              className="w-full"
            >
              <option value="" disabled>Select a flight</option>
              {flightNumbers.map(fn => <option key={fn} value={fn}>{fn}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="flightDate" className="block text-sm font-medium text-slate-400 mb-1">Flight Date</label>
            <input
              type="date"
              id="flightDate"
              value={etdDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>
      
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
          <div className="flex items-center">
            <input
              id="crewAndPilotsArrivedTogether"
              type="checkbox"
              checked={!!flightData.crewAndPilotsArrivedTogether}
              onChange={(e) => handleCheckboxChange('crewAndPilotsArrivedTogether', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="crewAndPilotsArrivedTogether" className="ml-2 text-sm font-medium text-slate-300">Crew &amp; Pilots Arrived Together</label>
          </div>
          <div className="flex items-center gap-2">
              <div className="flex items-center">
              <input
                  id="immigrationApproved"
                  type="checkbox"
                  checked={!!flightData.immigrationApproved}
                  onChange={(e) => handleCheckboxChange('immigrationApproved', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="immigrationApproved" className="ml-2 text-sm font-medium text-slate-300">Immigration Approved</label>
              </div>
              {flightData.immigrationApproved && (
                  <input
                      type="text"
                      placeholder="Add a comment..."
                      value={flightData.immigrationComment || ''}
                      onChange={(e) => updateFlightData('immigrationComment', e.target.value)}
                      className="py-1 px-2 text-sm"
                  />
              )}
          </div>
        </div>
        <div className="flex items-center flex-wrap justify-end gap-2">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <button onClick={() => setConfirmAction('systemClear')} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm">System Clear</button>
          <button onClick={() => setConfirmAction('gateClear')} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm">Gate Clear</button>
          <button
            onClick={handleCaptureClick}
            disabled={isCapturing}
            className="flex items-center bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:bg-slate-500"
          >
            <CameraIcon className="w-5 h-5 mr-2" />
            {isCapturing ? 'Capturing...' : 'Capture Document'}
          </button>
        </div>
      </div>

      {user && [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT].includes(user.role) && (
        <NewsInputField />
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {STATIONS.map((station, index) => {
            const crewTimestamp = getLatestTimestamp(station, 'crew');
            const pilotTimestamp = getLatestTimestamp(station, 'pilots');
            const showPilotButton = STATIONS.indexOf(station) <= STATIONS.indexOf('At Aircraft');

            return (
                <div key={station} data-tour-id={index === 0 ? 'crew-movement-station' : undefined} className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg flex flex-col p-3 transition-all duration-300 hover:border-blue-500/50 hover:shadow-blue-500/10">
                    <h3 className="font-semibold text-center text-slate-100 mb-2 text-sm h-10 flex items-center justify-center">{station}</h3>
                    <div className="flex-grow flex items-start justify-center space-x-2">
                        <div className="flex-1 flex flex-col items-center space-y-2">
                            <button 
                                onClick={() => handleTimestamp(station, 'crew')}
                                className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 ${crewTimestamp ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.6)]' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                                aria-label={`Record crew time for ${station}`}
                            >
                                <CrewIcon className="w-6 h-6"/>
                            </button>
                            <div className="h-4 text-xs font-mono text-slate-400 flex items-center justify-center gap-1">
                                <span>
                                    {crewTimestamp ? new Date(crewTimestamp.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}) : '--:--'}
                                </span>
                                {crewTimestamp && (
                                    <button 
                                        onClick={() => setClearConfirm({ station, type: 'crew' })} 
                                        className="text-slate-500 hover:text-red-400"
                                        aria-label={`Clear latest crew time for ${station}`}
                                    >
                                        <XCircleIcon className="w-3 h-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {showPilotButton && (
                            <>
                                <div className="h-16 w-px bg-slate-700"></div>
                                <div className="flex-1 flex flex-col items-center space-y-2">
                                    <button 
                                        onClick={() => handleTimestamp(station, 'pilots')}
                                        className={`w-12 h-12 flex items-center justify-center rounded-full transition-all duration-200 ${pilotTimestamp ? 'bg-amber-500 text-white shadow-[0_0_15px_rgba(245,158,11,0.6)]' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                                        aria-label={`Record pilot time for ${station}`}
                                    >
                                        <PilotIcon className="w-6 h-6"/>
                                    </button>
                                    <div className="h-4 text-xs font-mono text-slate-400 flex items-center justify-center gap-1">
                                        <span>
                                          {pilotTimestamp ? new Date(pilotTimestamp.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}) : '--:--'}
                                        </span>
                                        {pilotTimestamp && (
                                            <button 
                                                onClick={() => setClearConfirm({ station, type: 'pilots' })} 
                                                className="text-slate-500 hover:text-red-400"
                                                aria-label={`Clear latest pilot time for ${station}`}
                                            >
                                                <XCircleIcon className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    {station === '1st X-ray' && (
                        <div className="mt-2 pt-2 border-t border-slate-600">
                            <label htmlFor="finalFuel" className="block text-center text-sm font-medium text-amber-400 mb-1">Final Fuel ⛽️</label>
                            <input
                              id="finalFuel"
                              type="text"
                              value={flightData.counterDistribution.finalFigures['Final Fuel'] || ''}
                              onChange={(e) => updateCounterDistributionField('finalFigures', 'Final Fuel', e.target.value)}
                              className="w-full text-center"
                              placeholder="Enter value"
                            />
                        </div>
                    )}
                     {station === 'Doors Closed' && (
                      <div className="mt-auto pt-2 border-t border-slate-600 w-full">
                        <button
                          onClick={logFinalizeTimestamp}
                          disabled={!!flightData.finalizeTimestamp}
                          className={`w-full mt-2 py-1.5 rounded-lg text-sm font-bold transition-colors duration-200 ${
                            flightData.finalizeTimestamp
                              ? 'bg-purple-600 text-white cursor-default'
                              : 'bg-amber-600 hover:bg-amber-700 text-white'
                          }`}
                        >
                          {flightData.finalizeTimestamp
                            ? `Finalized @ ${new Date(flightData.finalizeTimestamp.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                            : 'Finalize'}
                        </button>
                      </div>
                    )}
                </div>
            )
        })}
      </div>

      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h2 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">ATSR</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="atsrNo" className="block text-sm font-medium text-slate-400 mb-1">ATSR NO</label>
            <input id="atsrNo" type="text" value={flightData.atsr?.['ATSR NO'] || ''} onChange={(e) => updateAtsrField('ATSR NO', e.target.value)} className="w-full" />
          </div>
          <div>
            <label htmlFor="excessBaggage" className="block text-sm font-medium text-slate-400 mb-1">Excess Baggage</label>
            <input id="excessBaggage" type="text" value={flightData.atsr?.['Excess Baggage'] || ''} onChange={(e) => updateAtsrField('Excess Baggage', e.target.value)} className="w-full" />
          </div>
          <div>
            <label htmlFor="heavyWeight" className="block text-sm font-medium text-slate-400 mb-1">Heavy Weight</label>
            <input id="heavyWeight" type="text" value={flightData.atsr?.['Heavy Weight'] || ''} onChange={(e) => updateAtsrField('Heavy Weight', e.target.value)} className="w-full" />
          </div>
          <div>
            <label htmlFor="aup" className="block text-sm font-medium text-slate-400 mb-1">AUP</label>
            <input id="aup" type="text" value={flightData.atsr?.['AUP'] || ''} onChange={(e) => updateAtsrField('AUP', e.target.value)} className="w-full" />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="atsrNotes" className="block text-sm font-medium text-slate-400 mb-1">Notes</label>
            <textarea id="atsrNotes" value={flightData.atsr?.['Notes'] || ''} onChange={(e) => updateAtsrField('Notes', e.target.value)} className="w-full" rows={2} />
          </div>
        </div>
      </div>
      
      {clearConfirm && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
              <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                  <h2 className="text-xl font-bold text-white mb-4">Confirm Clear</h2>
                  <p className="text-slate-300 mb-6">
                      Are you sure you want to clear the latest <strong className="capitalize">{clearConfirm.type}</strong> time for <strong className="text-amber-400">{clearConfirm.station}</strong>? This action cannot be undone.
                  </p>
                  <div className="flex justify-center gap-4">
                      <button onClick={() => setClearConfirm(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
                      <button onClick={handleClearTimestamp} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">Confirm & Clear</button>
                  </div>
              </div>
          </div>
      )}
       {confirmAction && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in">
            <div className="bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-center border border-slate-700">
                <h2 className="text-xl font-bold text-white mb-4">{confirmText.title}</h2>
                <p className="text-slate-300 mb-6">{confirmText.body}</p>
                <div className="flex justify-center gap-4">
                    <button onClick={() => setConfirmAction(null)} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg">Cancel</button>
                    <button onClick={handleConfirmAction} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg">Confirm</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default CrewMovementScreen;